/**
 * Prepares buttons for the text roll hover effect (see hover-effects.css).
 *
 * The label is wrapped in an element and duplicated, so the copy can slide in from below while the label slides out.
 * Only buttons whose content is plain text are touched. Buttons with icons or other markup (close buttons, slideshow
 * controls, add to cart) are left alone, as are buttons opting out with `data-no-roll`.
 * The script does nothing on devices that cannot hover.
 */
const BUTTON_SELECTOR = '.button, .button-secondary';
const SKIP_SELECTOR =
  '.button-unstyled, .close-button, .skip-to-content-link, .shopify-payment-button__button, [data-no-roll]';

/**
 * @param {HTMLElement} button
 */
function wrapButton(button) {
  if (button.matches(SKIP_SELECTOR) || button.querySelector('.button-roll')) return;

  const label = button.textContent?.trim();
  const isPlainText = [...button.childNodes].every((node) => node.nodeType === Node.TEXT_NODE);

  if (!label || !isPlainText) return;

  const roll = document.createElement('span');
  roll.className = 'button-roll';

  const text = document.createElement('span');
  text.className = 'button-roll__text';
  text.textContent = label;

  const copy = /** @type {HTMLElement} */ (text.cloneNode(true));
  copy.setAttribute('aria-hidden', 'true');

  roll.append(text, copy);
  button.replaceChildren(roll);
}

/**
 * @param {Node} node
 */
function wrapButtonsIn(node) {
  if (node instanceof HTMLElement) {
    if (node.matches(BUTTON_SELECTOR)) wrapButton(node);
    node.querySelectorAll(BUTTON_SELECTOR).forEach((button) => wrapButton(/** @type {HTMLElement} */ (button)));
    return;
  }

  // Text that changed inside an existing button, e.g. its label was replaced
  const button = node.parentElement?.closest(BUTTON_SELECTOR);
  if (button instanceof HTMLElement) wrapButton(button);
}

if (window.matchMedia('(hover: hover)').matches) {
  wrapButtonsIn(document.body);

  // Buttons are added and re-rendered by sections, the cart drawer and the theme editor
  new MutationObserver((records) => {
    for (const record of records) {
      record.addedNodes.forEach(wrapButtonsIn);
    }
  }).observe(document.body, { childList: true, subtree: true });
}
